import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material'
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../hooks/useToast'

interface ShareDialogProps {
  open: boolean
  onClose: () => void
  url: string
  title: string
  text?: string
  onShareSelect?: () => void
}

const ShareDialog: React.FC<ShareDialogProps> = ({ open, onClose, url, title, text, onShareSelect }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { showSuccess } = useToast()

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url)
    showSuccess(t('share.copied', 'Link in die Zwischenablage kopiert!'))
    if (onShareSelect) onShareSelect()
    onClose()
  }

  const openShareWindow = (shareUrl: string) => {
    window.open(shareUrl, '_blank', 'width=600,height=400')
    if (onShareSelect) onShareSelect()
    onClose()
  }

  const shareOptions = [
    {
      id: 'copy',
      label: t('share.copyLink', 'Link kopieren'),
      icon: <ContentCopyIcon />,
      action: handleCopyLink,
      color: theme.palette.text.primary
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: <WhatsAppIcon />,
      action: () => openShareWindow(`https://wa.me/?text=${encodeURIComponent(`${title} - ${url}`)}`),
      color: '#25D366'
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: <FacebookIcon />,
      action: () => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`),
      color: '#1877F2'
    },
    {
      id: 'twitter',
      label: 'X (Twitter)',
      icon: <TwitterIcon />,
      action: () => openShareWindow(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`),
      color: theme.palette.mode === 'dark' ? '#fff' : '#000'
    },
    {
      id: 'instagram',
      label: 'Instagram',
      icon: <InstagramIcon />,
      action: () => {
        navigator.clipboard.writeText(url)
        showSuccess(t('share.instagram', 'Link kopiert! Du kannst ihn nun auf Instagram teilen.'))
        if (onShareSelect) onShareSelect()
        onClose()
      },
      color: '#E1306C'
    }
  ]

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 3, minWidth: 320 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          {t('share.title', 'Gedicht teilen')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <List sx={{ pt: 0 }}>
          {shareOptions.map((option) => (
            <ListItem disablePadding key={option.id}>
              <ListItemButton onClick={option.action} sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ color: option.color }}>
                  {option.icon}
                </ListItemIcon>
                <ListItemText primary={option.label} primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  )
}

export default ShareDialog
